import * as authActions from './auth.actions';
import { createReducer, on, Action, ActionReducer } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { IGlimpseUser } from 'src/app/modules/auth/models/IGlimpseUser';
import { IFeatureResponse } from '../../modules/customer-shared/models/IFeatureResponse';

export interface AuthState {
  user: IGlimpseUser | null;
  userLoading: boolean;
  token: string | null;
  impersonatedAs: {
    user: IGlimpseUser | null;
    token: string | null;
  } | null;

  loginForm: {
    loading: boolean;
    error: string | null;
  };

  passwordResetRequestForm: {
    loading: boolean;
    error: string | null;
    success: boolean | null;
  };

  passwordResetForm: {
    loading: boolean;
    error: string | null;
  };
  features: {
    recentCount: number;
    list: IFeatureResponse[];
  };
}

export const initialState: AuthState = {
  user: null,
  userLoading: false,
  token: null,
  impersonatedAs: null,
  loginForm: {
    loading: false,
    error: null,
  },
  passwordResetRequestForm: {
    loading: false,
    error: null,
    success: false,
  },
  passwordResetForm: {
    loading: false,
    error: null,
  },
  features: {
    recentCount: 0,
    list: [],
  },
};

const reducer = createReducer(
  initialState,
  on(authActions.login, state => ({
    ...state,
    loginForm: {
      loading: true,
      error: null as string | null,
    },
  })),
  on(authActions.loginSuccess, (state, { user, token }) => ({
    ...state,
    user,
    token,
    loginForm: {
      loading: false,
      error: null as string | null,
    },
  })),
  on(authActions.logout, state => ({
    ...state,
    user: null as IGlimpseUser | null,
    token: null as string | null,
  })),
  on(authActions.loginFailed, (state, action) => ({
    ...state,
    user: null as IGlimpseUser | null,
    token: null as string | null,
    loginForm: {
      loading: false,
      error: action.error,
    },
  })),
  on(authActions.actionPasswordResetRequest, state => ({
    ...state,
    passwordResetRequestForm: {
      loading: true,
      error: null as string | null,
      success: null as boolean | null,
    },
  })),
  on(authActions.actionPasswordResetRequestSuccess, state => ({
    ...state,
    passwordResetRequestForm: {
      loading: false,
      error: null as string | null,
      success: true,
    },
  })),
  on(authActions.actionPasswordResetRequestFailed, (state, { error }) => ({
    ...state,
    passwordResetRequestForm: {
      loading: false,
      error: error ?? 'Failed to reset your password. Please try again.',
      success: false,
    },
  })),

  on(authActions.actionPasswordResetSubmit, state => ({
    ...state,
    passwordResetForm: {
      loading: true,
      error: null as string | null,
    },
  })),
  on(authActions.actionPasswordResetSuccess, state => ({
    ...state,
    passwordResetForm: {
      loading: false,
      error: null as string | null,
    },
  })),
  on(authActions.actionPasswordResetFailed, (state, action) => ({
    ...state,
    passwordResetForm: {
      loading: false,
      error: action.error || 'Failed to reset your password. Please try again.',
    },
  })),
  on(authActions.updateUser, (state, { user }) => ({
    ...state,
    user: { ...user },
  })),
  on(authActions.impersonationSuccess, (state, { user, token }) => ({
    ...state,
    impersonatedAs: {
      user,
      token,
    },
  })),
  on(authActions.pushEnable, state => ({ ...state, userLoading: true })),
  on(authActions.pushEnableSuccess, state => ({ ...state, userLoading: false })),
  on(authActions.pushEnableFail, state => ({ ...state, userLoading: false })),
  on(authActions.pushDisable, state => ({ ...state, userLoading: true })),
  on(authActions.pushDisableSuccess, state => ({ ...state, userLoading: false })),
  on(authActions.pushDisableFail, state => ({ ...state, userLoading: false })),
  on(authActions.finishImpersonation, state => ({ ...state, impersonatedAs: null as AuthState['impersonatedAs'] })),
  on(authActions.loadProfile, state => ({ ...state, userLoading: true })),
  on(authActions.loadProfileSuccess, (state, { user }) => ({ ...state, userLoading: false, user })),
  on(authActions.loadProfileFail, state => ({ ...state, userLoading: false })),
  on(authActions.loadFeaturesSuccess, (state, { features, recentCount }) => ({
    ...state,
    features: { recentCount: recentCount, list: features },
  })),
);

export function authReducer(state: AuthState | undefined, action: Action) {
  return reducer(state, action);
}

export function authStorageSync(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: [{ auth: ['user', 'token', 'impersonatedAs'] }],
    rehydrate: true,
    storageKeySerializer: key => 'glimpse_' + key,
  })(reducer);
}
